import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import OpenIcon from "@mui/icons-material/OpenInNewOutlined"

import { userApi } from "../../api"
import { usePermissions } from "../../permissions"
import { LoadingRows } from "../../components/LoadingState"
import UserAvatar from "../../components/UserAvatar"
import { formatDate } from "../../utils/format"

/**
 * Who this row is.
 *
 * ── Why a dialog and not a link straight to the page ────────────────────────
 *
 * A report row is a question mid-thought: "who is D. Okafor, and are these
 * hours theirs?" Navigating away to answer it costs the range, the filters and
 * the place in the table — and coming back means setting all three up again. So
 * the answer arrives over the report, and the page is the step after it for
 * anybody who wants more than an answer.
 *
 * ── Why it asks the server rather than using the row ────────────────────────
 *
 * The row has a name and an avatar because that is what a report needs. Role,
 * organization, whether the account is still active — the things that decide
 * what to make of the hours — are not in it. Where the viewer cannot read the
 * directory, the dialog says so and still shows the name it was given, because
 * half an answer beats a spinner that never resolves.
 */
export default function PersonDialog({ userId, fallbackName, onClose }) {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const mayRead = can("users", "view")

  const [person, setPerson] = useState(null)
  const [state, setState] = useState("idle")

  useEffect(() => {
    if (!userId || !mayRead) return undefined
    let live = true
    setState("loading")
    setPerson(null)
    userApi
      .get(userId)
      .then((data) => {
        if (!live) return
        setPerson(data.user || null)
        setState("ready")
      })
      .catch(() => live && setState("error"))
    return () => {
      live = false
    }
  }, [userId, mayRead])

  const name = person?.name || fallbackName || "This person"

  return (
    <Dialog open={Boolean(userId)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <UserAvatar name={name} avatarKey={person?.avatar_key} size={44} tooltip={false} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="p" noWrap>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {person?.title || person?.role_label || "Team member"}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {!mayRead ? (
          <Typography variant="body2" color="text.secondary">
            Their hours are above. Reading the rest of somebody's profile needs the Users module.
          </Typography>
        ) : state === "loading" ? (
          <LoadingRows rows={3} height={26} />
        ) : state === "error" || !person ? (
          <Typography variant="body2" color="text.secondary">
            That profile could not be loaded.
          </Typography>
        ) : (
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            <Field label="Signs in as" value={person.username} />
            <Field label="Email" value={person.email} />
            <Field label="Role" value={person.role_label || person.role_name} />
            <Field label="Organization" value={person.organization_label || person.organization_id} />
            {person.department_label ? <Field label="Department" value={person.department_label} /> : null}
            {person.phone ? <Field label="Phone" value={person.phone} /> : null}
            <Field
              label="Last seen"
              value={person.last_access_on ? formatDate(person.last_access_on) : "Never signed in"}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
                Account
              </Typography>
              <Chip
                size="small"
                label={person.status === "invited" ? "Invited" : person.active ? "Active" : "Deactivated"}
                color={person.status === "invited" ? "warning" : person.active ? "success" : "default"}
                sx={{ height: 20, fontSize: 11 }}
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {mayRead ? (
          <Button
            variant="contained"
            endIcon={<OpenIcon />}
            onClick={() => {
              onClose()
              navigate(`/users/${userId}`)
            }}
          >
            Open profile
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

function Field({ label, value }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Stack>
  )
}
